(function () {
  "use strict";

  // =====================
  // 配置
  // =====================
  const DEBUG = false; // 调试开关
  // 严格模式：只有当 URL 识别到系列且 H1 二次匹配确认一致，才执行注入；否则跳过并还原
  const REQUIRE_H1_CONFIRM = true;

  const log = (...args)=> { if (DEBUG) console.log("[LCSeries]", ...args); };
  const warn = (...args)=> { if (DEBUG) console.warn("[LCSeries]", ...args); };

  if (window.LCSeries?.mounted) { log("already mounted, skip"); return; }
  window.LCSeries = { mounted: false };

  // =========================
  // 1) URL 解析（带别名归一化 + 兼容多语言前缀）
  // =========================
  const SERIES_SLUG_ALIASES = {
    // 规范化为 SERIES_COPY_MAP 的 key
    "linen-curtains": "linen",
    "bedroom-curtains": "bedroom",
    "blackout-curtains": "blackout",
    "cotton-curtains": "cotton",
    "kitchen-curtains": "kitchen",
    "living-room-curtains": "living-room",
    "outdoor-curtains": "outdoor",
    "polyester-curtains": "polyester",
    "room-darkening-curtains": "room-darkening",
    "sheer-curtains": "sheer",
    "study-office": "study-den-office",
    "study-den-office-curtains": "study-den-office",
    // 可按需继续补充
  };

  function normalizeSlug(slug) {
    if (!slug) return null;
    const s = String(slug).toLowerCase();
    // 先映射别名，再校验是否在文案表中
    const canonical = SERIES_SLUG_ALIASES[s] || s;
    const MAP = (window.SERIES_COPY_MAP || SERIES_COPY_MAP);
    return MAP[canonical] ? canonical : null;
  }

  function getSeriesSlugFromPath(pathname = location.pathname) {
    try {
      // 移除尾部斜杠，解码中文或 URL 编码
      const path = decodeURIComponent(String(pathname).replace(/\/+$/, ""));
      // 兼容 /en/collections/slug 或 /en-US/collections/slug
      const m = path.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/([^\/?#]+)$/i);
      const raw = m ? m[1] : null;
      const normalized = normalizeSlug(raw);
      return normalized; // 只有在文案 MAP 中的 slug 才返回，否则 null
    } catch (e) {
      warn("getSeriesSlugFromPath error:", e);
      return null;
    }
  }

  // =========================
  // 2) 文案对象（保持原有内容，不做改动）
  // =========================
  /* 1) BLACKOUT */
  const COPY_BLACKOUT = {
    h1: "Custom Peaceful Blackout Curtains",
    h2: "Block Light, Block Noise, and Bring Comfort to Your Space",
    body: [
      "Our blackout curtains are crafted from thick, high-quality fabrics that block out light and reduce outside noise, perfect for bedrooms, nurseries, or any space where you want to create a peaceful and quiet environment.",
      "With exceptional light-blocking power, they help transform your room into a serene retreat—perfect for better sleep and complete privacy. Available in a variety of colors and styles, from classic solids to elegant velvet and charming floral or patterned designs. Custom sizes are available for sliding doors, large windows, or unique spaces.",
      "Let MrsAwesome blackout curtains add comfort, peace, and style to your home, making it the perfect place to relax and unwind."
    ],
    faqs: [
      { q: "Do blackout curtains completely block out light?", a: "Yes, these heavy fabrics effectively block light for a dark environment—great for daytime sleep and privacy. For even better results, add a blackout liner (e.g., 3-pass coating up to ~99%)." },
      { q: "Do blackout curtains help with soundproofing?", a: "They reduce outside noise and create a quieter indoor atmosphere. For high-level soundproofing, additional measures may be required." },
      { q: "Do blackout curtains help with energy efficiency?", a: "They help keep rooms warmer in winter and cooler in summer, reducing HVAC use and saving energy." },
      { q: "Will blackout curtains affect air circulation?", a: "Thicker, sealed designs can reduce airflow. If ventilation matters, consider breathable blackout fabric or open partially when full darkness isn’t needed." },
      { q: "Any tips for installation?", a: "For max blocking, make width 6–12 inches wider than the window and extend length to sill or floor coverage." },
      { q: "Does color affect light-blocking?", a: "Yes. Darker colors usually block more light. For full blackout, use darker tones or add lining." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 2) COTTON */
  const COPY_COTTON = {
    h1: "Custom Cotton Curtains for Every Room",
    h2: "Light and Airy or Heavy and Private – Perfect for Your Needs",
    body: [
      "Upgrade your home with our custom cotton curtains, available in two styles to fit your needs. Whether you want a light, airy feel or need full light-blocking, our organic cotton curtains offer both beauty and function.",
      "Lightweight Bamboo Texture Cotton Curtains: organic cotton with a calm, natural look—ideal for kitchens/living rooms; breathable, easy-care, light filtering with privacy.",
      "Heavyweight Luxe Velvet Cotton Blackout Curtains: for a hotel-like vibe; block light and reduce noise—ideal for bedrooms/home theaters."
    ],
    faqs: [
      { q: "Do cotton curtains block out light?", a: "Standard cotton filters light; for stronger control choose velvet cotton blackout or add a lining." },
      { q: "How do I care for cotton curtains?", a: "Machine wash cold with mild detergent when allowed; otherwise hand wash/dry clean. Air dry to avoid shrink/fade." },
      { q: "Do cotton curtains fade?", a: "Prolonged sun may fade cotton; consider lining or darker colors to resist sun damage." },
      { q: "Are cotton curtains eco-friendly?", a: "Yes. Cotton is natural/biodegradable; our organic cotton options carry GRS & Oeko-Tex certifications." },
      { q: "How do I choose the right size?", a: "Width = 1.5–2× window width; length to floor or slight puddle. For sizing help, email support@mrs-awesome.com." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 3) KITCHEN */
  const COPY_KITCHEN = {
    h1: "Custom Made Kitchen Curtains",
    h2: "Lightweight and Practical Kitchen Curtains, Made to Measure",
    body: [
      "Add style and functionality to your kitchen with our custom kitchen curtains. Lightweight, easy to clean, and designed to filter light while helping reduce bacterial growth.",
      "From airy sheers for natural light to blackout options for privacy, our made-to-measure service ensures a perfect fit for any kitchen window.",
      "Choose from a wide range of colors and materials, from café curtains to tailored drapes—durable, easy to maintain, and ideal for daily use."
    ],
    faqs: [
      { q: "Are sheer curtains suitable for the kitchen?", a: "Yes—lightweight, breathable, promote airflow, and help reduce moisture buildup." },
      { q: "Can sheers block smoke/odors?", a: "No—sheers filter light, not smoke. Use smoke-resistant fabrics or clean regularly." },
      { q: "Are sheers easy to clean?", a: "Most are machine-washable with mild detergent and cold water." },
      { q: "Suitable for large windows?", a: "Absolutely—custom sizing fits small to large windows." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 4) LIVING ROOM */
  const COPY_LIVING_ROOM = {
    h1: "Custom Living Room Curtains",
    h2: "Stylish, Custom-Made Curtains to Elevate Your Living Room",
    body: [
      "Enhance your living room with custom curtains—from light sheers for an airy feel to room darkening panels for a cozy, private space.",
      "High-quality fabrics balance beauty and practicality. Choose modern grommets or elegant pleats for durable, easy-care comfort and style.",
      "Custom-made to fit any window for a flawless look, with light control, privacy, and energy efficiency that suit your décor."
    ],
    faqs: [
      { q: "How do I choose the right living room curtains?", a: "For privacy/light control use blackout; for bright rooms use sheer/semi-sheer." },
      { q: "How to size length/width?", a: "Length to floor or ~1\" above; width 1.5–2× window for graceful pleats." },
      { q: "Which fabric to choose?", a: "Velvet/Cotton-Linen for blocking & insulation; Linen/Sheer for breathable light." },
      { q: "How to clean?", a: "Cotton/Poly: gentle machine cold; Velvet/Linen: dry-clean to protect texture/color." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 5) OUTDOOR */
  const COPY_OUTDOOR = {
    h1: "Durable Custom Outdoor Curtains",
    h2: "Durable Outdoor Curtains for Shade, Privacy, and Style",
    body: [
      "Made from waterproof, UV-resistant Olefin, our outdoor curtains withstand the elements—colors stay vibrant after 500+ hours of sunlight.",
      "Perfect for patios, gazebos, decks, and pergolas—reduce glare, add shade, and enhance privacy.",
      "Header options: grommet/back-tab; bottom tiebacks/rings for wind stability. Tailored fits for pergolas or patios."
    ],
    faqs: [
      { q: "Are fabrics weatherproof & UV-resistant?", a: "Yes—Olefin is durable, UV-protective, and wear-resistant with strong fade resistance." },
      { q: "Waterproof vs water-resistant?", a: "Waterproof blocks water; water-resistant handles light/angled rain but isn’t sealed." },
      { q: "Rain & mold?", a: "Treated to resist water/mold; during storms store/retract and keep ventilated; clean regularly." },
      { q: "Grommet vs back-tab?", a: "Grommets slide easily & fit thicker rods; back-tabs hide the rod—choose by rod size, wind, and style." },
      { q: "Wind handling?", a: "Bottom ties/loops reduce movement; retract for storms; resistance depends on installation/fastening." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 6) POLYESTER */
  const COPY_POLYESTER = {
    h1: "Custom Stylish Polyester Curtains",
    h2: "Find the Perfect Polyester Curtains for Any Space",
    body: [
      "Explore a wide range—from polyester blackout to airy sheers and bold solids/patterns—covering every room and style.",
      "Durable, easy-care fabrics span outdoor-ready options to chic indoor looks, blending seamlessly with home décor.",
      "Custom service available, with expert consultation to tailor perfect curtains. Fabrics carry GRS & Oeko-Tex 100 certifications."
    ],
    faqs: [
      { q: "Static issues?", a: "Polyester can build static in dry seasons; use a humidifier or anti-static spray." },
      { q: "New-fabric smell?", a: "A slight odor may occur and fades in days—air before hanging." },
      { q: "Care?", a: "Vacuum with soft brush; follow label for machine wash/dry clean; avoid high heat." },
      { q: "Soundproofing?", a: "Heavier polyester reduces noise somewhat; for more, choose soundproof/lined options." },
      { q: "Pet-friendly?", a: "Durable and tear-resistant, but can attract hair—clean regularly." },
      { q: "Fading?", a: "Good color retention; prolonged sun can fade—use darker shades or lining in strong sun." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 7) ROOM DARKENING */
  const COPY_ROOM_DARKENING = {
    h1: "Custom Room Darkening Curtains",
    h2: "Stylish, Light-Reducing Curtains for Every Room",
    body: [
      "A balance of light control and style—reduce light while keeping a fresh, airy feel—great for bedrooms, living rooms, and offices.",
      "Choose colors like gray, blue, navy, white, and materials such as cotton, velvet, and linen—fits sliding doors and small windows.",
      "Header options include grommet and pleated for modern or traditional interiors."
    ],
    faqs: [
      { q: "Do they block all light?", a: "Typically ~70–80% light reduction. For full darkness choose Blackout curtains (>95%)." },
      { q: "Where to use?", a: "Ideal for spaces needing controlled light and privacy, while staying bright/airy." },
      { q: "UV reduction?", a: "Yes—especially darker/lined options. For full UV protection, consider Blackout." },
      { q: "Fabric choice impact?", a: "Heavier fabrics (velvet/cotton) block more; lighter (linen) moderate. Add lining for more control." },
      { q: "Layering?", a: "Pair with sheers for daytime light; add blackout liners for extra privacy/control." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 8) SHEER */
  const COPY_SHEER = {
    h1: "Custom Elegant Sheer Curtains",
    h2: "Light, Airy, and Effortlessly Elegant, Brighten Your Space",
    body: [
      "Let natural light flow with just-right privacy—perfect for living rooms, bedrooms (layered), kitchens, or café nooks.",
      "Styles from simple solids and natural textures to embroidered and floral sheers, in greens, pinks, blues, whites, and creams.",
      "Custom service ensures the perfect fit—floor-length panels, door sheers, or small kitchen sheers tailored to your space."
    ],
    faqs: [
      { q: "How much light do sheers block? Sun protection?", a: "They filter/soften light and reduce glare/UV somewhat; for blackout/heat blocking, layer with heavy curtains." },
      { q: "Care & frequency?", a: "Vacuum brush or gentle shake; most hand/delicate-cycle washable. Typical wash every 3–6 months; more often in kitchens/pet/smoking areas." },
      { q: "Pair with blackout?", a: "Yes—common day/night combo." },
      { q: "Shrinkage after washing?", a: "Minimal when following care; customs allow extra seam allowance." },
      { q: "Best rooms?", a: "Living/dining, bedrooms (with blackout), kitchens, cafés—anywhere you want soft, inviting light." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 9) STUDY / DEN / OFFICE */
  const COPY_STUDY_DEN_OFFICE = {
    h1: "Custom Study / Den / Office Curtains",
    h2: "The Ideal Choice for an Efficient Work Environment",
    body: [
      "Perfect light control and privacy for a comfortable, productive workspace—premium materials blend style and function.",
      "Options from sheer to blackout to fit lighting preferences and focus needs. Eco-friendly, durable, easy-to-clean fabrics for long use.",
      "Minimalist, elegant designs complement any office or study with ease."
    ],
    faqs: [
      { q: "How is light controlled?", a: "Pick sheers for natural light or blackout for control; double-layer (panel + lining) adds flexibility." },
      { q: "Do they insulate?", a: "Heavier fabrics offer heat insulation; double-layers/thermal linings improve efficiency." },
      { q: "Productivity benefits?", a: "Right lighting reduces eye strain/distraction, improving focus and productivity." },
      { q: "Delivery time?", a: "Std. 5–7 days; customs may take longer. Need it sooner? Contact us for rush options." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* 10) VELVET */
  const COPY_VELVET = {
    h1: "Custom Velvet Blackout Curtains",
    h2: "Luxurious Velvet Curtains in a Variety of Styles and Colors for Your Room",
    body: [
      "Heavy velvet delivers style and practicality—excellent light blocking for bedrooms, living rooms, or home theaters with a quiet, cozy feel.",
      "Wide color range: red, beige, blue, green, and more—classic neutrals or bold shades to complement your space.",
      "For stronger control, add lining. Multiple header styles help craft a truly custom look."
    ],
    faqs: [
      { q: "Best rooms for velvet?", a: "Bedrooms/living rooms/home theaters where light control and some sound reduction are needed." },
      { q: "Do velvet curtains block all light?", a: "Lined heavy velvet can nearly fully block outside light—ideal for dark, peaceful rooms." },
      { q: "Kids/pets friendly?", a: "Velvet may attract dust/hair—clean regularly and avoid tugging to extend lifespan." },
      { q: "How to clean?", a: "Vacuum with soft brush; dry-clean for deep cleaning to preserve texture/color." },
      { q: "Prevent wrinkles?", a: "Low-heat iron or simply hang; wrinkles relax over time." },
      { q: "Weight considerations?", a: "Use reinforced rod/hardware for heavier panels to hang evenly and securely." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  // — LINEN
  const COPY_LINEN = {
    h1: "Custom Natural Linen Curtains",
    h2: "Personalized Linen Curtains in Multiple Styles, Colors, and Sizes",
    body: [
      "Crafted from premium Belgian linen, our curtains feature a natural texture that is lightweight, breathable, hypoallergenic, and durable—ideal for everyday living.",
      "Choose unlined panels for an airy, light-filtering look, or add cotton or blackout lining for privacy, light control, thermal efficiency, and UV protection.",
      "Available header styles: pleated, grommet, back-tab, and rod-pocket. Every panel is sewn with reinforced seams and clean edges for a tailored drape.",
      "Colorways include neutrals—beige, cream, ivory, blue, gray—and bold tones—green, yellow, red, magenta, warm brown—suiting living rooms, kitchens, and bedrooms.",
      "All sizes supported: from short café curtains to extra-long drops and extra-wide panels. Order single panels or full sets; each piece is tailored to your exact measurements.",
      "Bring timeless style and natural elegance to your home with truly custom Belgian linen curtains."
    ],
    faqs: [
      { q: "What are the benefits of linen curtains?", a: "Linen curtains are strong and durable, eco-friendly and sustainable, hypoallergenic, breathable, and moisture-absorbent. They help keep your home warm in winter and cool in summer while adding a natural, elegant texture to any room." },
      { q: "Do linen curtains need ironing?", a: "Linen naturally has a slight wrinkle texture that adds to its charm. You can lightly steam or iron on a low setting if you want a neater look. Hanging curtains will also help smooth out wrinkles over time." },
      { q: "How do I wash linen curtains?", a: "Regularly vacuum to remove dust. Most linen curtains can be machine washed on a gentle cycle with cold water and mild detergent. For delicate or custom curtains, hand washing is best. Always air dry away from direct sunlight and avoid high heat to prevent shrinking or fading." },
      { q: "Do linen curtains need lining?", a: "Lining is recommended if you want better privacy, light control, insulation, or to protect the fabric from sun damage. Without lining, linen curtains are lighter and allow more natural light through, offering a breezy and soft look." },
      { q: "How can I tell if linen curtains are high quality?", a: "Check the fiber content—preferably 100% linen or a high linen blend. Look for a tight weave, soft yet strong fabric, even natural coloring, and clean, durable stitching. Quality craftsmanship ensures long-lasting curtains." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  // — BEDROOM
  const COPY_BEDROOM = {
    h1: "Custom Tranquil Bedroom Curtains",
    h2: "Tailored Curtains for a Peaceful and Cozy Bedroom",
    body: [
      "Create a peaceful and comfortable atmosphere in your bedroom with our custom bedroom curtains. Stylish and practical, these curtains perfectly balance privacy, light control, and luxury. Whether you need blackout bedroom curtains to ensure complete darkness for better sleep, or light-filtering curtains to gently soften the light, we have you covered.",
      "Our custom bedroom curtains come in various fabric options, including thick, durable velvet for excellent soundproofing and insulation, perfect for a quieter bedroom, or natural, breathable linen that’s lightweight and easy to operate. For those who want a touch of natural light, we also offer combinations of sheer curtains and blackout curtains, ensuring privacy without sacrificing light.",
      "Each curtain is custom-made to fit your window size perfectly, enhancing both comfort and aesthetics in your bedroom, and ensuring a peaceful and relaxing sleep environment."
    ],
    faqs: [
      { q: "What’s the ideal length and width for bedroom curtains?", a: "Ideally, bedroom curtains should touch the floor or hang about 1 inch above it. If you prefer a more dramatic look, you can extend the length by 6 to 16 inches. For width, the total curtain width should be 1.5 to 2 times the window width to ensure full coverage and a graceful pleated effect when closed." },
      { q: "Do bedroom curtains need a lining?", a: "Lining enhances the curtains' light-blocking ability and provides extra privacy and insulation. If you want to completely block out light, we recommend choosing curtains with a lining." },
      { q: "Can bedroom curtains block sound?", a: "Yes, thicker curtains like velvet or lined options can effectively reduce outside noise, making them ideal for noisy environments or for improving bedroom privacy." },
      { q: "Can I return or exchange curtains if they’re too big or small?", a: "We offer custom curtains to ensure a perfect fit for your windows. If the curtains you purchased don't meet your expectations, please refer to our return and exchange policy. We recommend confirming your window measurements before purchasing or contacting our customer service for assistance." }
    ],
    contactMail: "support@mrs-awesome.com"
  };

  /* ===== 系列 -> 文案映射（slug 按集合 handle 设置） ===== */
  const SERIES_COPY_MAP = {
    linen: COPY_LINEN,
    bedroom: COPY_BEDROOM,
    blackout: COPY_BLACKOUT,
    cotton: COPY_COTTON,
    kitchen: COPY_KITCHEN,
    "living-room": COPY_LIVING_ROOM,
    outdoor: COPY_OUTDOOR,
    polyester: COPY_POLYESTER,
    "room-darkening": COPY_ROOM_DARKENING,
    sheer: COPY_SHEER,
    "study-den-office": COPY_STUDY_DEN_OFFICE,
    velvet: COPY_VELVET
  };

  // =========================
  // 3) H1 关键词，仅用于 URL 成功后的二次确认
  // =========================
  const SERIES_H1_KEYWORDS = {
    linen: [ /\blinen\b/i, /\bflax\b/i ],
    bedroom: [ /\bbedroom\b/i, /\bsleep\b/i ],
    blackout: [ /\bblackout\b/i, /total\s*dark/i ],
    cotton: [ /\bcotton\b/i, /organic\s*cotton/i ],
    kitchen: [ /\bkitchen\b/i, /café\s*curtain/i ],
    "living-room": [ /living\s*room/i, /\blounge\b/i ],
    outdoor: [ /\boutdoor\b/i, /(patio|pergola|gazebo)/i ],
    polyester: [ /\bpolyester\b/i, /\bpoly\b/i ],
    "room-darkening": [ /room\s*dark(en|ening)/i, /\bdarkening\b/i ],
    sheer: [ /\bsheer\b/i, /\bvoile\b/i, /semi[\s-]*sheer/i ],
    "study-den-office": [ /\bstudy\b|\boffice\b|home\s*office|\bden\b/i ],
    velvet: [ /\bvelvet\b/i, /\bvelour\b/i ]
  };

  function getBannerH1Node() {
    const wrap = document.querySelector(".collection-banner--content.text-center");
    if (!wrap) return null;
    return wrap.querySelector('h1.collection-banner--title') ||
           wrap.querySelector('.collection-banner--title h1') ||
           wrap.querySelector('h1');
  }

  function getOriginalH1Text() {
    if (window.LCSeries.origH1) return window.LCSeries.origH1;
    const h1 = getBannerH1Node();
    const txt = (h1 && h1.textContent || "").trim();
    if (txt) window.LCSeries.origH1 = txt;
    return window.LCSeries.origH1 || "";
  }

  function detectSeriesFromH1(h1Text) {
    if (!h1Text) return null;
    for (const [slug, patterns] of Object.entries(SERIES_H1_KEYWORDS)) {
      if (patterns.some(re => re.test(h1Text))) return slug;
    }
    return null;
  }

  const $  = (s, r) => (r||document).querySelector(s);
  const $$ = (s, r) => Array.prototype.slice.call((r||document).querySelectorAll(s));
  function el(tag, cls, txt, dataLc){
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    if (dataLc) n.setAttribute("data-lc", dataLc);
    return n;
  }

  // =========================
  // 4) 注入/清理/还原
  // =========================
  function cleanInjected() {
    $$('.linen-intro-above-grid, [data-lc="linen-intro"]').forEach(n => n.remove());
    $$('.collection-extra-textbox.linen-textbox, [data-lc="linen-textbox"]').forEach(n => n.remove());
  }

  function restoreH1Text() {
    const wrap = $(".collection-banner--content.text-center");
    if (!wrap) return;
    const h1 = wrap.querySelector('h1.collection-banner--title')
           || wrap.querySelector('.collection-banner--title h1')
           || wrap.querySelector('h1');
    if (h1 && window.LCSeries.origH1) {
      h1.textContent = window.LCSeries.origH1;
    }
  }

  function replaceH1Text(copy) {
    const wrap = $(".collection-banner--content.text-center");
    if (!wrap) return;
    $$('h1[data-lc="linen-h1"]', wrap).forEach(n => n.remove());
    const h1 = wrap.querySelector('h1.collection-banner--title')
           || wrap.querySelector('.collection-banner--title h1')
           || wrap.querySelector('h1');
    if (h1) {
      log("replaceH1Text →", copy.h1);
      h1.textContent = copy.h1;
    }
  }

  // function injectIntroAboveGrid(copy){ return; }{
  //   const grid = document.getElementById("ProductGridContainer");
  //   if (!grid) return;
  //   const wrap = el("section", "linen-intro-above-grid", null, "linen-intro");
  //   wrap.appendChild(el("h2", "lc-linen-h2", copy.h2, "linen-h2"));
  //   (copy.body || []).forEach(txt => wrap.appendChild(el("p","lc-linen-body",txt,"linen-body")));
  //   grid.parentNode.insertBefore(wrap, grid);
  //   log("injected intro above grid");
  // }

  function buildTailSection(copy){
    const section = el("section","collection-extra-textbox linen-textbox",null,"linen-textbox");
    section.setAttribute("aria-label","Series FAQ");
    const inner = el("div","linen-textbox__inner",null,"linen-textbox-inner");
    section.appendChild(inner);

    const faq = el("section","linen-faq",null,"linen-faq");
    const faqTitle = `FAQ – ${copy.h1?.replace(/^Custom\s+/i, "") || "Curtains"}`;
    faq.appendChild(el("h3","linen-faq__title", faqTitle, "linen-faq-title"));

    (copy.faqs || []).forEach(it=>{
      const det = el("details","linen-faq__item",null,"linen-faq-item");
      det.open = true; det.setAttribute("open","");
      det.appendChild(el("summary","linen-faq__q",it.q,"linen-faq-q"));
      det.appendChild(el("div","linen-faq__a",it.a,"linen-faq-a"));
      faq.appendChild(det);
    });

    // === contactMail 作为 FAQ 的最后一条 ===
    const mail = (copy.contactMail || "support@mrsawesome.com").trim();
    const detMail = el("details","linen-faq__item",null,"linen-faq-item");
    detMail.open = true; detMail.setAttribute("open","");
    detMail.appendChild(el("summary","linen-faq__q","Need help with sizing or customization?","linen-faq-q"));
    const ans = el("div","linen-faq__a",null,"linen-faq-a");
    const a = document.createElement("a");
    a.href = "mailto:" + mail;
    a.textContent = mail;
    ans.append("We’d love to help you find the perfect fit! Just email us at ", a, ", and we’ll be happy to assist you.");
    detMail.appendChild(ans);
    faq.appendChild(detMail);

    inner.appendChild(faq);
    return section;
  }

  function injectTail(copy) {
    // 外层容器
    const section = el("section", "collection-extra-textbox linen-textbox", null, "linen-textbox");
    section.setAttribute("aria-label", "Series Content + FAQ");
    const inner = el("div", "linen-textbox__inner", null, "linen-textbox-inner");
    section.appendChild(inner);

    // === 上方内容区（正文） ===
    const intro = el("div", "linen-intro-content", null, "linen-intro");
    intro.appendChild(el("h2", "lc-linen-h2", copy.h2, "linen-h2"));
    (copy.body || []).forEach(txt =>
      intro.appendChild(el("p", "lc-linen-body", txt, "linen-body"))
    );
    inner.appendChild(intro);

    // === FAQ 区 ===
    const faq = el("section", "linen-faq", null, "linen-faq");
    const faqTitle = `FAQ – ${copy.h1?.replace(/^Custom\s+/i, "") || "Curtains"}`;
    faq.appendChild(el("h3", "linen-faq__title", faqTitle, "linen-faq-title"));

    (copy.faqs || []).forEach(it => {
      const det = el("details", "linen-faq__item", null, "linen-faq-item");
      det.open = true;
      det.setAttribute("open", "");
      det.appendChild(el("summary", "linen-faq__q", it.q, "linen-faq-q"));
      det.appendChild(el("div", "linen-faq__a", it.a, "linen-faq-a"));
      faq.appendChild(det);
    });

    // === 联系方式 ===
    const mail = (copy.contactMail || "support@mrsawesome.com").trim();
    const detMail = el("details", "linen-faq__item", null, "linen-faq-item");
    detMail.open = true;
    detMail.setAttribute("open", "");
    detMail.appendChild(
      el("summary", "linen-faq__q", "Need help with sizing or customization?", "linen-faq-q")
    );
    const ans = el("div", "linen-faq__a", null, "linen-faq-a");
    const a = document.createElement("a");
    a.href = "mailto:" + mail;
    a.textContent = mail;
    ans.append("We’d love to help you find the perfect fit! Just email us at ", a, ", and we’ll be happy to assist you.");
    detMail.appendChild(ans);
    faq.appendChild(detMail);

    inner.appendChild(faq);

    // === 插入到页面中 ===
    const grid = document.getElementById("ProductGridContainer");
    if (grid?.parentNode) {
      grid.insertAdjacentElement("afterend", section);
      log("injected intro + FAQ after grid");
    } else {
      const fallback =
        $(".collection-container.section-spacing-bottom") ||
        $(".collection-container") ||
        $(".CollectionMain, .collection, main .container, .page-width");
      if (fallback) fallback.appendChild(section);
      else warn("no container found for tail section");
    }
  }



  // =========================
  // 5) 渲染 & 路由监听（严格 URL → H1 二次匹配）
  // =========================
  function render() {
    // 缓存原始 H1
    if (!window.LCSeries.origH1) getOriginalH1Text();

    const currentUrl = location.href;
    const slugFromUrl = getSeriesSlugFromPath(); // 只会返回 MAP 中已存在的规范 slug

    // 先清理旧注入，避免残留
    cleanInjected();

    // Step 1: 必须 URL 命中
    if (!slugFromUrl) {
      warn("URL not matched or slug not in MAP → skip injection");
      restoreH1Text();
      return; // 完全跳过
    }

    // Step 2: H1 二次确认（仅在 REQUIRE_H1_CONFIRM=true 时生效）
    let finalSlug = slugFromUrl;
    const origH1 = window.LCSeries.origH1 || getOriginalH1Text();
    const h1Detected = detectSeriesFromH1(origH1);

    if (REQUIRE_H1_CONFIRM) {
      if (!h1Detected) {
        warn("H1 not detected → skip injection (strict mode)");
        restoreH1Text();
        return;
      }
      if (h1Detected !== slugFromUrl) {
        warn("URL/H1 mismatch →", { urlSlug: slugFromUrl, h1Slug: h1Detected, h1: origH1 });
        restoreH1Text();
        return;
      }
      // 二者一致：使用 URL 确认后的 slug
      finalSlug = slugFromUrl;
    } else {
      // 非严格模式：优先 URL，H1 仅作为提示，不改变最终 slug
      if (h1Detected && h1Detected !== slugFromUrl) {
        log("H1 suggests different series, but prefer URL →", { urlSlug: slugFromUrl, h1Slug: h1Detected });
      }
      finalSlug = slugFromUrl;
    }

    // 取文案并注入
    const copy = (window.SERIES_COPY_MAP || SERIES_COPY_MAP)[finalSlug];
    if (!copy) {
      warn("copy not found for slug:", finalSlug);
      restoreH1Text();
      return;
    }

    window.COPY = copy;
    window.__LC_SERIES_SLUG__ = finalSlug;

    replaceH1Text(copy);
    // injectIntroAboveGrid(copy);
    injectTail(copy);

    log("render finished for slug:", finalSlug, "@", currentUrl);
  }

  function watchRoute(){
    let prev = location.href;
    const tick = () => {
      if (location.href !== prev) {
        log("route changed:", prev, "→", location.href);
        prev = location.href;
        // 变更后重置原始 H1，再渲染
        window.LCSeries.origH1 = "";
        setTimeout(() => { getOriginalH1Text(); render(); }, 50);
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  function injectStyles(){
    if (document.getElementById("lc-linen-style")) return;
    const css = `
:root{ --lc-maxw: 1900px; }

/* Banner H1 容器 */
.collection-banner--content.text-center{
  max-width: 1200px !important;
  margin-left:auto; margin-right:auto;
}
.collection-banner--content.text-center .collection-banner--title{
  max-width:none !important; width:100% !important;
  white-space:normal !important; overflow:visible !important; text-overflow:clip !important;
  font-size:48px !important; line-height:1.2 !important;
}

/* Grid 上方简介 */
.linen-intro-above-grid{
  max-width: var(--lc-maxw);
  margin:30px auto;
}
.linen-intro-above-grid .lc-linen-h2{
  margin:0 0 16px; font-weight:700;
  font-size:clamp(20px,2.4vw,30px); line-height:1.25; letter-spacing:.2px; text-align:left;
}
.linen-intro-above-grid .lc-linen-body{
  margin:12px 0; font-size:clamp(15px,1.6vw,18px);
  line-height:1.75; color:#2f2f2f; max-width:100%; text-align:left;
}

/* 全宽 FAQ（无边框） */
.linen-textbox{
  max-width:none; width:100vw; position:relative; left:50%; right:50%;
  margin-left:-50vw; margin-right:-50vw;
  margin-top:24px; margin-bottom:50px;
  padding:0 clamp(16px,5vw,64px);
}
.linen-textbox__inner{ display:block; max-width: var(--lc-maxw); margin:0 auto; }
.linen-faq{ background:transparent; border:none; padding: clamp(12px,3vw,24px) 0; }
.linen-faq__title{ margin:0 0 clamp(10px,2vw,16px); font-weight:700; font-size:clamp(20px,2.4vw,26px); text-align:left; }
.linen-faq__item{ border:none; background:transparent; padding: clamp(10px,2vw,14px) 0; margin:0; border-bottom:1px solid rgba(0,0,0,.08); }
.linen-faq__item:last-of-type{ border-bottom:none; }
.linen-faq__q{ cursor:pointer; font-weight:600; line-height:1.4; text-align:left; font-size:clamp(16px,1.8vw,18px); list-style:none; padding:4px 0; }
.linen-faq__a{ margin-top:8px; padding-bottom:4px; color:#333; line-height:1.75; font-size:clamp(14px,1.6vw,16px); text-align:left; }
.linen-faq__item summary::-webkit-details-marker{ display:none; }
.linen-faq__item summary{ list-style:none; }

/* 联系信息 */
.linen-contact{ margin-top: clamp(16px,3vw,24px); padding-top: clamp(16px,3vw,24px); border-top:1px solid rgba(0,0,0,.08); font-size:clamp(14px,1.6vw,16px); color:#555; }
.linen-contact a{ color:#2563eb; text-decoration:none; }
.linen-contact a:hover{ text-decoration:underline; }

/* 手机端隐藏顶部简介块 */
@media (max-width: 640px){
  .linen-intro-above-grid{ display: none !important; margin: 0 !important; padding: 0 !important; }
}
`;
    const s = document.createElement("style");
    s.id = "lc-linen-style";
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
    log("styles injected");
  }

  // =========================
  // 6) 启动
  // =========================
  function boot(){
    log("boot start, url =", location.href);

    injectStyles();

    // 等待横幅/H1 或 Grid 出现后再渲染（避免早期为空）
    const mo = new MutationObserver(() => {
      const bannerReady = !!$(".collection-banner--content.text-center");
      const gridReady   = !!document.getElementById("ProductGridContainer");
      if (bannerReady || gridReady) {
        mo.disconnect();
        log("dom ready → render");
        render();
      }
    });
    mo.observe(document.documentElement, { childList:true, subtree:true });

    // 双保险：定时尝试
    setTimeout(() => { log("fallback render @800ms"); render(); }, 800);

    // 监听无刷新路由
    watchRoute();

    window.LCSeries.mounted = true;
    log("boot finished");
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once:true });
  } else {
    boot();
  }
})();




(function(){
  /* ================= 配置（按需改） ================= */
  const CFG = {
    product: {
      anchorSelector: '.product-title-container',
      heading: '',
      limit: 12,
      gridClass: 'atp-grid'
    },
    collection: {
      cardTitleSelector: 'a.product-card-title',
      cardHeading: '',
      perCardLimit: 8,
      gridClass: 'ct-grid'
    },
    homepage: {
      enabled: true
    },
    makeLink: true,
    newTab: false,
    concurrent: 6,
    linkMap: {}
  };

  /* ================= 工具函数 ================= */
  function isProductPage(){ return /\/products\//.test(location.pathname); }
  function isCollectionPage(){ return /\/collections\//.test(location.pathname); }
  function isHomePage(){ return location.pathname === '/' || /^\/[a-z]{2}(?:-[a-z]{2})?\/?$/.test(location.pathname); }

  function getProductHandle(){
    try { const h = window.ShopifyAnalytics?.meta?.product?.handle; if (h) return h; } catch(e){}
    const m = location.pathname.match(/\/products\/([^\/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function tagToHandle(t){
    return t.toLowerCase().trim()
      .replace(/[^\w\u00C0-\u024F\u4E00-\u9FFF- ]+/g,'')
      .replace(/\s+/g,'-');
  }
  function linkFor(tag){
    if (CFG.linkMap && CFG.linkMap[tag]) return CFG.linkMap[tag];
    return `/collections/all/${encodeURIComponent(tagToHandle(tag))}`;
  }

  async function fetchTagsByHandle(handle){
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' });
    if (!res.ok) return [];
    const p = await res.json();
    return Array.from(new Set((p.tags || []).map(x => (x ?? '').toString().trim()).filter(Boolean)));
  }

  /* ============ 屏蔽规则 ============ */
  const DEFAULT_BLOCK_RULES = (window.CFG && window.CFG.block && Array.isArray(window.CFG.block.default))
    ? window.CFG.block.default : [];

  const BLOCK_ENABLED = window.CFG?.block?.enabled !== false;

  function compileRule(ruleRaw){
    const rule = String(ruleRaw || '').trim();
    if (!rule) return null;
    if (rule.startsWith('/') && rule.lastIndexOf('/') > 0){
      const last = rule.lastIndexOf('/');
      try { const re = new RegExp(rule.slice(1,last), rule.slice(last+1) || 'i'); return s=>re.test(s); }
      catch(_) { return null; }
    }
    if (rule.startsWith('=')){ const t = rule.slice(1).toLowerCase(); return s=> s.toLowerCase() === t; }
    if (rule.startsWith('^')){ const p = rule.slice(1).toLowerCase(); return s=> s.toLowerCase().startsWith(p); }
    if (rule.includes('*')){ const m = rule.replace(/\*/g,'').toLowerCase(); if (!m) return null; return s=> s.toLowerCase().includes(m); }
    return s=> s.toLowerCase() === rule.toLowerCase();
  }
  const BLOCK_MATCHERS = DEFAULT_BLOCK_RULES.map(compileRule).filter(Boolean);

  function isBlocked(tag){
    if (!BLOCK_ENABLED || !tag || !BLOCK_MATCHERS.length) return false;
    for (const fn of BLOCK_MATCHERS){ try{ if (fn(tag)) return true; }catch(_){} }
    return false;
  }

  /* ================== 组件构建 ================== */
  function buildBlock(tags, heading, gridClass){
    const wrap = document.createElement('div');
    wrap.className = 'at-wrap';
    wrap.dataset.atBlock = 'product'; // 明确标记类型
    wrap.dataset.atTimestamp = Date.now(); // 添加时间戳用于调试

    const title = (heading ?? '').trim();
    if (title) {
      const h = document.createElement('h3');
      h.className = 'at-heading';
      h.textContent = title;
      wrap.appendChild(h);
    }

    const grid = document.createElement('div');
    grid.className = gridClass;

    (tags || []).forEach(tag=>{
      const a = document.createElement('a');
      a.className = 'at-chip';
      if (CFG.makeLink){
        a.href = linkFor(tag);
        if (CFG.newTab){ a.target = '_blank'; a.rel = 'noopener'; }
      } else {
        a.href = '#';
        a.addEventListener('click', e => e.preventDefault());
      }
      a.textContent = tag;
      grid.appendChild(a);
    });

    wrap.appendChild(grid);
    return wrap;
  }

  function buildCardBlock(tags){
    const wrap = document.createElement('div');
    wrap.className = 'ct-tags';
    wrap.dataset.ctBlock = 'card'; // 明确标记类型
    wrap.dataset.ctTimestamp = Date.now();

    const title = (CFG.collection.cardHeading ?? '').trim();
    if (title) {
      const h = document.createElement('div');
      h.className = 'ct-heading';
      h.textContent = title;
      wrap.appendChild(h);
    }

    const grid = document.createElement('div');
    grid.className = CFG.collection.gridClass;

    tags.slice(0, CFG.collection.perCardLimit).forEach(tag=>{
      const a = document.createElement('a');
      a.className = 'ct-chip';
      if (CFG.makeLink){
        a.href = linkFor(tag);
        if (CFG.newTab){ a.target = '_blank'; a.rel = 'noopener'; }
      } else {
        a.href = '#';
        a.addEventListener('click', e => e.preventDefault());
      }
      a.textContent = tag;
      grid.appendChild(a);
    });

    wrap.appendChild(grid);
    return wrap;
  }

  /* ============= 公用函数 ============= */
  function getHandleFromHref(href){
    const m = (href || '').match(/\/products\/([^\/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function isClonedOrHidden(el){
    return !!(
      el.closest('.slick-cloned') ||
      el.closest('.swiper-slide-duplicate') ||
      el.closest('[data-cloned="true"]') ||
      el.closest('[aria-hidden="true"]') ||
      el.closest('.is-clone') ||
      el.closest('.glide__slide--clone') ||
      el.closest('.flickity-viewport [aria-hidden="true"]')
    );
  }

  /* ================== 缓存与并发控制 ================== */
  let running = 0;
  const queue = [];
  async function takeToken(){
    if (running < CFG.concurrent){ running++; return; }
    await new Promise(resolve => queue.push(resolve));
    running++;
  }
  function releaseToken(){
    running--;
    const next = queue.shift();
    if (next) next();
  }

  const TAGS_CACHE = new Map();
  const INFLIGHT = new Map();
  async function getTagsCached(handle){
    if (TAGS_CACHE.has(handle)) return TAGS_CACHE.get(handle);
    if (INFLIGHT.has(handle)) return await INFLIGHT.get(handle);
    const p = fetchTagsByHandle(handle).then(tags=>{
      TAGS_CACHE.set(handle, tags || []);
      INFLIGHT.delete(handle);
      return TAGS_CACHE.get(handle);
    }).catch(err=>{
      INFLIGHT.delete(handle);
      throw err;
    });
    INFLIGHT.set(handle, p);
    return await p;
  }

  /* ================== 产品页渲染 - 完全重写防重复逻辑 ================== */
  const PRODUCT_PAGE_STATE = {
    rendered: false,
    handle: null,
    locked: false  // 防止并发
  };

  // 检查整个文档是否已有产品标签块
  function hasProductTagsInDOM(){
    return !!(
      document.querySelector('.at-wrap[data-at-block="product"]') ||
      document.querySelector('[data-at-inserted="1"]')
    );
  }

  async function renderProductPage(){
    // 1. 基础检查
    if (!isProductPage()) return;
    if (PRODUCT_PAGE_STATE.locked) return; // 防止并发
    if (PRODUCT_PAGE_STATE.rendered) return; // 已渲染
    if (hasProductTagsInDOM()) {
      PRODUCT_PAGE_STATE.rendered = true;
      return;
    }

    const anchor = document.querySelector(CFG.product.anchorSelector);
    if (!anchor) return;

    const handle = getProductHandle();
    if (!handle) return;
    
    // 2. 检查是否是同一个产品（防止 SPA 路由切换）
    if (PRODUCT_PAGE_STATE.handle && PRODUCT_PAGE_STATE.handle !== handle) {
      // 不同产品，重置状态
      PRODUCT_PAGE_STATE.rendered = false;
      PRODUCT_PAGE_STATE.handle = null;
    }
    
    if (PRODUCT_PAGE_STATE.rendered && PRODUCT_PAGE_STATE.handle === handle) {
      return; // 同一个产品已渲染
    }

    // 3. 加锁
    PRODUCT_PAGE_STATE.locked = true;

    try{
      // 4. 再次检查 DOM（防止异步期间被插入）
      if (hasProductTagsInDOM()) {
        PRODUCT_PAGE_STATE.rendered = true;
        PRODUCT_PAGE_STATE.handle = handle;
        return;
      }

      // 5. 获取标签
      let tags = await getTagsCached(handle);
      if (!tags.length) {
        PRODUCT_PAGE_STATE.rendered = true;
        PRODUCT_PAGE_STATE.handle = handle;
        return;
      }
      
      tags = tags.filter(t => !isBlocked(t)).slice(0, CFG.product.limit);
      if (!tags.length) {
        PRODUCT_PAGE_STATE.rendered = true;
        PRODUCT_PAGE_STATE.handle = handle;
        return;
      }

      // 6. 最终检查并插入
      if (!hasProductTagsInDOM()) {
        const block = buildBlock(tags, CFG.product.heading, CFG.product.gridClass);
        block.dataset.atInserted = '1';
        anchor.insertAdjacentElement('afterend', block);
        
        console.log('[auto-tags] ✓ Product tags inserted for:', handle);
      }
      
      // 7. 标记完成
      PRODUCT_PAGE_STATE.rendered = true;
      PRODUCT_PAGE_STATE.handle = handle;
      
    }catch(e){
      console.warn('[auto-tags] product error:', e);
    }finally{
      // 8. 解锁
      PRODUCT_PAGE_STATE.locked = false;
    }
  }

  /* ================== 卡片处理 ================== */
  const BOUND_LINKS = new WeakSet();
  const PROCESSED_CARDS = new WeakSet();

  const IO = new IntersectionObserver((entries)=>{
    entries.forEach(e=>{
      if (!e.isIntersecting) return;
      IO.unobserve(e.target);
      processOne(e.target).catch(()=>{});
    });
  }, { rootMargin: '300px' });

  function bindCardTitlesInScope(scope){
    const links = Array.from(scope.querySelectorAll(CFG.collection.cardTitleSelector));
    if (!links.length) return 0;

    let bound = 0;
    links.forEach(link=>{
      if (BOUND_LINKS.has(link)) return;
      if (isClonedOrHidden(link)) return;
      
      BOUND_LINKS.add(link);
      IO.observe(link);
      bound++;
    });
    return bound;
  }

  async function processOne(link){
    if (!link || isClonedOrHidden(link)) return;

    const cardRoot =
      link.closest('[data-product-card], .product-card, li, article, .card, .grid__item') ||
      link.parentElement;

    if (!cardRoot) return;
    
    // 防重复检查
    if (PROCESSED_CARDS.has(cardRoot)) return;
    if (cardRoot.dataset.ctMounted === '1') return;
    if (cardRoot.querySelector('.ct-tags')) return;
    if (cardRoot.querySelector('[data-ct-block="card"]')) return;
    
    // 立即标记
    PROCESSED_CARDS.add(cardRoot);
    cardRoot.dataset.ctMounted = '1';

    const href = link.getAttribute('href') || '';
    const handle = getHandleFromHref(href);
    if (!handle) return;

    await takeToken();
    try{
      let tags = await getTagsCached(handle);
      tags = (tags || []).filter(t => !isBlocked(t));
      if (tags.length){
        // 再次检查
        if (!cardRoot.querySelector('.ct-tags') && !cardRoot.querySelector('[data-ct-block="card"]')) {
          const block = buildCardBlock(tags);
          link.insertAdjacentElement('afterend', block);
        }
      }
    }catch(e){
      console.warn('[auto-tags] card fetch error:', e);
    }finally{
      releaseToken();
    }
  }

  /* ================== 心跳轮询 - 产品页只执行一次 ================== */
  let HEARTBEAT_ID = null;
  
  function tick(){
    try{
      // 产品页：只在未渲染时执行一次
      if (isProductPage()) {
        if (!PRODUCT_PAGE_STATE.rendered && !hasProductTagsInDOM()) {
          renderProductPage();
        }
        // 产品页渲染后立即停止心跳
        if (PRODUCT_PAGE_STATE.rendered || hasProductTagsInDOM()) {
          stopHeartbeat();
          return;
        }
      }

      // 集合页/首页：持续查找新卡片
      if (isCollectionPage() || isHomePage()) {
        bindCardTitlesInScope(document);
      }
      
    }catch(e){
      console.warn('[auto-tags] heartbeat error:', e);
    }finally{
      HEARTBEAT_ID = setTimeout(tick, 3000); // 固定3秒
    }
  }

  function startHeartbeat(){
    if (HEARTBEAT_ID) return;
    HEARTBEAT_ID = setTimeout(tick, 100); // 首次100ms后执行
  }
  
  function stopHeartbeat(){
    if (HEARTBEAT_ID){ 
      clearTimeout(HEARTBEAT_ID); 
      HEARTBEAT_ID = null;
      console.log('[auto-tags] Heartbeat stopped');
    }
  }

  /* ================== 对外 API ================== */
  window.LCTags = window.LCTags || {};
  
  window.LCTags.refresh = function(scope){
    try{
      const root = scope instanceof Element ? scope : document;
      bindCardTitlesInScope(root);
      
      // 产品页手动刷新
      if (isProductPage() && !PRODUCT_PAGE_STATE.rendered && !hasProductTagsInDOM()) {
        renderProductPage();
      }
    }catch(e){}
  };
  
  // 路由切换时重置
  window.LCTags.reset = function(){
    PRODUCT_PAGE_STATE.rendered = false;
    PRODUCT_PAGE_STATE.handle = null;
    PRODUCT_PAGE_STATE.locked = false;
    stopHeartbeat();
    startHeartbeat();
    console.log('[auto-tags] State reset');
  };
  
  // 调试工具
  window.LCTags.debug = function(){
    // console.log('Product Page State:', PRODUCT_PAGE_STATE);
    // console.log('Has tags in DOM:', hasProductTagsInDOM());
    // console.log('Product blocks:', document.querySelectorAll('.at-wrap[data-at-block="product"]').length);
  };

  /* ================== 入口 ================== */
  function runInitial(){
    // 首次运行前先检查
    if (isProductPage() && hasProductTagsInDOM()) {
      PRODUCT_PAGE_STATE.rendered = true;
      PRODUCT_PAGE_STATE.handle = getProductHandle();
      console.log('[auto-tags] Tags already exist, skipping render');
    } else {
      renderProductPage();
    }
    
    bindCardTitlesInScope(document);
    startHeartbeat();
  }

  // 页面卸载清理
  window.addEventListener('beforeunload', ()=>{
    stopHeartbeat();
    IO.disconnect();
  });
  
  // 初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitial);
  } else {
    // DOM 已就绪，延迟一点执行（等待 Shopify 初始化）
    setTimeout(runInitial, 50);
  }
  
  // Shopify 主题编辑器
  document.addEventListener('shopify:section:load', ()=>{
    // 编辑器中重新加载时重置
    PRODUCT_PAGE_STATE.rendered = false;
    PRODUCT_PAGE_STATE.handle = null;
    setTimeout(runInitial, 100);
  });
  
  // 轮播图事件（防抖）
  let refreshTimeout;
  ['lc:carousel:refresh','swiper:refresh','slick:reInit','flickity:reload'].forEach(evt=>{
    document.addEventListener(evt, ()=> {
      clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(()=> {
        // 轮播刷新只处理卡片，不触发产品页
        if (isCollectionPage() || isHomePage()) {
          window.LCTags.refresh(document);
        }
      }, 300);
    });
  });
})();